import { useState, useEffect, useCallback } from "react";

export function useLocalStorage<T>(key: string, initialValue: T) {
  // Helper function to check if two values are deeply equal
  const isEqual = (a: any, b: any): boolean => {
    if (a === b) return true;

    if (Array.isArray(a) && Array.isArray(b)) {
      return (
        a.length === b.length &&
        a.every((item, index) => isEqual(item, b[index]))
      );
    }

    if (
      typeof a === "object" &&
      typeof b === "object" &&
      a !== null &&
      b !== null
    ) {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);

      return (
        keysA.length === keysB.length &&
        keysA.every((key) => isEqual(a[key], b[key]))
      );
    }

    return false;
  };

  const readValue = useCallback((): T => {
    if (typeof window === "undefined") {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  }, [key, initialValue]);

  const [storedValue, setStoredValue] = useState<T>(readValue);

  const setValue = useCallback(
    (value: T | ((val: T) => T)) => {
      try {
        const valueToStore =
          value instanceof Function ? value(storedValue) : value;

        // Only update if values are different
        if (!isEqual(valueToStore, storedValue)) {
          setStoredValue(valueToStore);

          if (typeof window !== "undefined") {
            window.localStorage.setItem(key, JSON.stringify(valueToStore));
          }
        }
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key, storedValue],
  );

  useEffect(() => {
    const newValue = readValue();
    if (!isEqual(newValue, storedValue)) {
      setStoredValue(newValue);
    }
  }, [readValue, storedValue]);

  return [storedValue, setValue] as const;
}
